import * as React from 'react';

import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import SearchIcon from '@mui/icons-material/Search';
import { IconButton, InputAdornment, useTheme } from '@mui/material';

import useDebounce from '@/hooks/useDebounce/useDebounce';
import { mapSearchClickEvent } from '@/utils';

import { getLocationSearchTextInputStyles } from '../LocationSearch/LocationSearch.styles';

interface PlaceType {
	description: string;
	place_id: string;
}

interface AutocompleteAddressTextFieldProps {
	value: string;
	onChange: (value: string) => void;
	onSearch: (value?: string) => void;
	level?: 'city' | 'full';
}

export default function AutocompleteAddressTextField({
	value,
	onChange,
	onSearch,
	level = 'city',
}: AutocompleteAddressTextFieldProps) {
	const theme = useTheme();
	const places = useMapsLibrary('places');
	const [options, setOptions] = React.useState<PlaceType[]>([]);
	const debouncedValue = useDebounce(value, 300);
	const placeholder = 'ZIP code, City or State';

	React.useEffect(() => {
		if (!places) return;

		const getPlacePredictions = async () => {
			if (!debouncedValue.trim()) {
				setOptions([]);
				return;
			}

			const service = new places.AutocompleteService();
			try {
				const response = await service.getPlacePredictions({
					input: debouncedValue,
					types: level === 'city' ? ['(cities)'] : ['geocode'],
					componentRestrictions: { country: 'US' },
				});
				setOptions(response?.predictions || []);
			} catch (error) {
				console.error('Error fetching place predictions:', error);
				setOptions([]);
			}
		};

		void getPlacePredictions();
	}, [places, debouncedValue, level]);

	const handleInputChange = (_: React.SyntheticEvent, newValue: string) => {
		onChange(newValue);
	};

	const handleOptionSelect = (_: React.SyntheticEvent, newValue: string | PlaceType | null, reason: string) => {
		if (newValue) {
			const selectedValue = typeof newValue === 'string' ? newValue : newValue.description;
			onChange(selectedValue);
			if (reason === 'selectOption') {
				mapSearchClickEvent();
				onSearch(selectedValue);
			}
		}
	};

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			mapSearchClickEvent();
			onSearch();
		}
	};

	const handleSearchClick = () => {
		mapSearchClickEvent();
		onSearch();
	};

	return (
		<Autocomplete
			fullWidth
			options={options}
			autoComplete
			includeInputInList
			filterSelectedOptions
			inputValue={value}
			value={value}
			freeSolo
			getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
			noOptionsText="No locations"
			onInputChange={handleInputChange}
			onChange={handleOptionSelect}
			renderInput={(params) => (
				<TextField
					{...params}
					placeholder={placeholder}
					onKeyDown={handleKeyPress}
					InputProps={{
						...params.InputProps,
						sx: { ...getLocationSearchTextInputStyles(theme), height: 'auto' },
						endAdornment: (
							<InputAdornment position="end">
								<IconButton
									onClick={handleSearchClick}
									data-testid="locations_search_button_input_icon"
									aria-label="Search"
								>
									<SearchIcon data-testid="locations_search_icon_search" />
								</IconButton>
							</InputAdornment>
						),
					}}
					inputProps={{
						...params.inputProps,
						'data-testid': 'locations_search_input_box',
						'aria-label': placeholder,
					}}
				/>
			)}
			renderOption={(props, option) => (
				<li {...props}>
					<Grid container alignItems="center">
						<Grid item sx={{ display: 'flex', width: 44 }}>
							<LocationOnIcon sx={{ color: 'text.secondary' }} />
						</Grid>
						<Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
							<Typography>{option.description}</Typography>
						</Grid>
					</Grid>
				</li>
			)}
		/>
	);
}
